// Toast controller
// Auto-dismisses toasts after a duration with slide-in animation
// Supports Escape dismiss and pause-on-hover
// Adapted from railsblocks.com/docs/toast

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    duration: { type: Number, default: 5000 },
  }

  connect() {
    this._paused = false

    // Slide in from the top (toasts render top-centered)
    this.element.style.transform = "translateY(-100%)"
    this.element.style.opacity = "0"

    requestAnimationFrame(() => {
      this.element.style.transition = "all 300ms ease-out"
      this.element.style.transform = "translateY(0)"
      this.element.style.opacity = "1"
    })

    // Auto-dismiss if duration is set
    if (this.durationValue > 0) {
      this._startAutoDismiss()
    }

    // Pause on hover
    this._mouseEnterHandler = this._pauseTimer.bind(this)
    this._mouseLeaveHandler = this._resumeTimer.bind(this)
    this.element.addEventListener("mouseenter", this._mouseEnterHandler)
    this.element.addEventListener("mouseleave", this._mouseLeaveHandler)

    // Escape key dismiss
    this._escapeHandler = this._handleEscape.bind(this)
    document.addEventListener("keydown", this._escapeHandler)
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    this.element.removeEventListener("mouseenter", this._mouseEnterHandler)
    this.element.removeEventListener("mouseleave", this._mouseLeaveHandler)
    document.removeEventListener("keydown", this._escapeHandler)
  }

  dismiss() {
    // Slide out to the top
    this.element.style.transform = "translateY(-100%)"
    this.element.style.opacity = "0"

    setTimeout(() => {
      this.element.remove()
    }, 300)
  }

  _startAutoDismiss() {
    this._remainingTime = this.durationValue
    this._timerStart = Date.now()
    this.timeout = setTimeout(() => {
      this.dismiss()
    }, this._remainingTime)
  }

  _pauseTimer() {
    this._paused = true
    if (this.timeout) {
      clearTimeout(this.timeout)
      // Calculate remaining time
      const elapsed = Date.now() - this._timerStart
      this._remainingTime = Math.max(0, this._remainingTime - elapsed)
    }
  }

  _resumeTimer() {
    this._paused = false
    if (this.durationValue > 0 && this._remainingTime > 0) {
      this._timerStart = Date.now()
      this.timeout = setTimeout(() => {
        this.dismiss()
      }, this._remainingTime)
    }
  }

  _handleEscape(event) {
    if (event.key === "Escape") {
      this.dismiss()
    }
  }
}
